import React, { useState, useMemo } from 'react';
import { Box, Typography, 
  FormControl, MenuItem, InputLabel, Select, 
} from '@mui/material';
import { getSortableValue, formatXValue } from 'state/helpers';
import StackedBarChart from './StackedBarChart';

const SortStackedBarChart = ({ 
  data = [], 
  title = "",
  bottomLegend, 
  leftLegend, 
  digit = 1,
  isEnableTotals = true, 
  isHorizontalBottomLegend, 
  topOffSet = 0,
  bottomOffSet = 0,
  bottomLegendOffSet = 0, 
  itemWidthOffSet = 0,
  translateXOffSet = 0,
  translateYOffSet = 0,
  itemsSpacingOffSet = 0,
}) => {  
  const [sortType, setSortType] = useState('xAxis');  
  
  const sortStackedChartDataByXAxis = (chartData, xAxisName) => {
    if (!Array.isArray(chartData)) {
      throw new TypeError("Expected chart data to be an array.");
    }

    if (!xAxisName || typeof xAxisName !== "string") {
      throw new TypeError("xAxisName should be a non-empty string.");
    }

    const sortedData = [...chartData] // tránh mutate
      .sort((a, b) => {
        const valueA = getSortableValue(a[xAxisName] || a["x"]);
        const valueB = getSortableValue(b[xAxisName] || b["x"]);

        if (typeof valueA === "string" && typeof valueB === "string") {
          return valueA.localeCompare(valueB);
        }
        return valueA - valueB;
      })
      .map((item) => ({
        ...item,
        [xAxisName]: formatXValue(item[xAxisName] || item["x"]),
      }));

    return sortedData;
  };
  
  // const sortChartDataByMultipleAxesPriority = (chartData, ...axisNames) => {
  //   return [...chartData]
  //     .sort((a, b) => {
  //       for (let axis of axisNames) {
  //         const valueA = a[axis];
  //         const valueB = b[axis];
  //         if (valueA < valueB) return -1;
  //         if (valueA > valueB) return 1;
  //       }
  //       return 0;
  //     })
  //     .map((item) => ({
  //       ...item,
  //       x: formatXValue(item.x),
  //     }));
  // };

  const sortChartDataByMultipleAxes = (chartData, ...axisNames) => {    
    return [...chartData]
      .sort((a, b) => {
        const sumA = axisNames.reduce(
          (sum, axis) => sum + (typeof a[axis] === "number" ? a[axis] : 0),
          0
        );
        const sumB = axisNames.reduce(
          (sum, axis) => sum + (typeof b[axis] === "number" ? b[axis] : 0),
          0
        );
        return sumA - sumB;
      })
      .map((item) => ({
        ...item,
        x: formatXValue(item.x),
      }));
  };
  
  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return null;
    }

    const keys = Object.keys(data[0]);
    
    if (sortType === "yAxis") {
      const axisNames = keys.filter(k => k !== "x"); // bỏ cột x
      if (axisNames.length > 0) {
        return sortChartDataByMultipleAxes(data, ...axisNames);
      }
      return data;
    } else if (sortType === "xAxis") {
      return sortStackedChartDataByXAxis(data, "x");
    }
    return data;
  }, [sortType, data]);

  if (!sortedData || sortedData.length === 0 || !Array.isArray(sortedData)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }

  
  return (
    <Box sx={{ height: '100%', width: '100%' }}>       
      <FormControl 
        size="small"
        sx={{ m: "0.5rem" }}
      >
        <InputLabel>Sort</InputLabel>
        <Select
          value={sortType}
          label="Sort"
          onChange={(e) => setSortType(e.target.value)}
        >
          <MenuItem value="xAxis">by xAxis</MenuItem>
          <MenuItem value="yAxis">by yAxis</MenuItem>                                            
        </Select>
      </FormControl> 
     
      <StackedBarChart 
        data={sortedData} 
        title={title}
        bottomLegend={bottomLegend} 
        leftLegend={leftLegend} 
        digit={digit}
        isHorizontalBottomLegend={isHorizontalBottomLegend}
        isEnableTotals={isEnableTotals}
        topOffSet={topOffSet}
        bottomOffSet={bottomOffSet}
        bottomLegendOffSet={bottomLegendOffSet} 
        itemWidthOffSet={itemWidthOffSet} 
        translateXOffSet={translateXOffSet}
        translateYOffSet={translateYOffSet}
        itemsSpacingOffSet={itemsSpacingOffSet}
      /> 
    </Box>
  );
};

export default SortStackedBarChart;
